import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Button,
  Card,
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '@predoc/cow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordRequirementsSection } from './PasswordRequirementsSection';
import { PasswordInput } from '@/components';
import { toast } from 'sonner';
import { Footer } from './Footer';
import { AUTH_ASSETS, AUTH_PATHS, AUTH_SESSION_KEYS } from '../constants';
import {
  newPasswordSchema,
  type MfaStep,
  type NewPasswordFormData,
} from '../types';
import { useConfirmSignIn } from '../hooks/authApi';
import { useMfa } from '../MfaContext';
import { fetchMfaSetupGuard } from '../hooks/useMfaSetupGuard';

export const NewPasswordRequired = () => {
  const navigate = useNavigate();
  const { mutateAsync, isPending, error } = useConfirmSignIn();
  const { authEmail, setOrganizationMfaEnabled, setTempCredentials } = useMfa();

  const form = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = form.watch('password');

  const redirectForMfaSetupIfRequired = useCallback(async () => {
    if (!authEmail) {
      return false;
    }

    const mfaGuard = await fetchMfaSetupGuard(authEmail);

    if (!mfaGuard.setupRequired) {
      return false;
    }

    navigate(AUTH_PATHS.MFA_SETUP_PHONE);
    return true;
  }, [authEmail, navigate]);

  // Navigate to login if user is not in new password required flow
  useEffect(() => {
    const validateAuthState = async () => {
      const hasNewPasswordRequiredFlag =
        sessionStorage.getItem(AUTH_SESSION_KEYS.NEW_PASSWORD_REQUIRED) ===
        'true';

      if (!hasNewPasswordRequiredFlag) {
        navigate(AUTH_PATHS.LOGIN);
        return;
      }

      try {
        const session = await fetchAuthSession();

        if (session.tokens?.accessToken) {
          sessionStorage.removeItem(AUTH_SESSION_KEYS.NEW_PASSWORD_REQUIRED);
          const redirectedToMfaSetup = await redirectForMfaSetupIfRequired();
          if (redirectedToMfaSetup) {
            return;
          }
          navigate(AUTH_PATHS.HOME);
        }
      } catch {
        toast.error('Failed to set new password.');
      }
    };

    validateAuthState();
  }, [navigate, redirectForMfaSetupIfRequired]);

  const onSubmit = async (data: NewPasswordFormData) => {
    try {
      const result = await mutateAsync({ password: data.password });
      const signInStep = result.nextStep.signInStep as MfaStep;

      if (
        signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
        signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
        signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
      ) {
        setTempCredentials(
          authEmail ? { username: authEmail, password: data.password } : null,
        );
        sessionStorage.removeItem(AUTH_SESSION_KEYS.NEW_PASSWORD_REQUIRED);
        toast.success('Verification Code Sent.');
        navigate(AUTH_PATHS.MFA_VERIFICATION);
        return;
      }

      if (signInStep !== 'DONE') {
        toast.error('Additional sign-in step required. Please login again.');
        navigate(AUTH_PATHS.LOGIN);
        return;
      }

      sessionStorage.removeItem(AUTH_SESSION_KEYS.NEW_PASSWORD_REQUIRED);
      toast.success('Password was set successfully.');

      const redirectedToMfaSetup = await redirectForMfaSetupIfRequired();
      if (redirectedToMfaSetup) {
        return;
      }

      setOrganizationMfaEnabled(false);
      navigate(AUTH_PATHS.HOME);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to set new password.';

      if (
        errorMessage.includes('No active sign-in session') ||
        errorMessage.includes('No pending sign-in') ||
        errorMessage.includes('Invalid session')
      ) {
        sessionStorage.removeItem(AUTH_SESSION_KEYS.NEW_PASSWORD_REQUIRED);
        navigate(AUTH_PATHS.LOGIN);
      }
      toast.error('Failed to set new password.');
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-between bg-white">
      <div className="flex w-full flex-1 flex-col items-center justify-center px-4">
        <img
          src={AUTH_ASSETS.FULL_LOGO_BLACK}
          alt="Predoc Logo"
          className="mb-8 h-14"
        />

        <Card className="w-full max-w-[464px] p-8">
          <h1 className="mb-6 text-2xl font-semibold tracking-tight">
            Set New Password
          </h1>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      data-testid="password-input"
                      className={
                        form.formState.errors.password
                          ? 'border-destructive'
                          : ''
                      }
                      aria-invalid={!!form.formState.errors.password}
                      aria-describedby={
                        form.formState.errors.password
                          ? 'password-error'
                          : undefined
                      }
                      {...form.register('password')}
                    />
                  </FormControl>
                </FormItem>

                <PasswordRequirementsSection password={password} />

                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      data-testid="confirm-password-input"
                      className={
                        form.formState.errors.confirmPassword
                          ? 'border-destructive'
                          : ''
                      }
                      aria-invalid={!!form.formState.errors.confirmPassword}
                      aria-describedby={
                        form.formState.errors.confirmPassword
                          ? 'confirm-password-error'
                          : undefined
                      }
                      {...form.register('confirmPassword')}
                      showPasswordLabel="Show confirm password"
                      hidePasswordLabel="Hide confirm password"
                    />
                  </FormControl>
                  {form.formState.errors.confirmPassword && (
                    <FormMessage
                      id="confirm-password-error"
                      className="text-destructive mt-1 text-sm"
                      role="alert"
                    >
                      {form.formState.errors.confirmPassword.message}
                    </FormMessage>
                  )}
                </FormItem>
              </div>

              {error && (
                <div
                  className="text-destructive text-center text-sm"
                  role="alert"
                >
                  {error.message}
                </div>
              )}

              <Button
                type="submit"
                disabled={isPending || !form.formState.isValid}
                className="h-11 w-full"
                loading={isPending}
              >
                {isPending ? 'Setting Password...' : 'Set Password'}
              </Button>
            </form>
          </Form>
        </Card>
      </div>
      <Footer />
    </div>
  );
};
